import { createRootRouteWithContext } from '@tanstack/react-router';
import { authnClient } from '@frontend/auth';
import {
  getJTI,
  getLoginData,
  getUser,
  getWeaveToken,
  isWeaveTokenBufferActive,
  type PortalUser,
} from '@frontend/auth-helpers';
import { http } from '@frontend/fetch';

type Context = {
  authenticated: boolean;
  user: PortalUser | undefined;
  lastLocationId: string | undefined;
};

export const Route = createRootRouteWithContext<Context>()({
  beforeLoad: async () => {
    const token = getWeaveToken();
    const jti = getJTI();
    const useJTI = await authnClient.shouldUseJTI();
    http.setUseJti(useJTI);
    http.setAuthorizationHeader(token, jti);

    const authenticated = authnClient.isUserAuthenticated() || isWeaveTokenBufferActive();
    const storedUserMatchesTokenUser = authnClient.storedUserIsLoggedInUser();
    const storedUser = getUser();

    const user: PortalUser | undefined =
      token && !!storedUser && storedUserMatchesTokenUser ? storedUser : await fetchUser();
    const lastLocationId = user ? getLoginData(user.userID)?.latestLocationIds?.[0] : undefined;
    /**
     * Data returned here will be available on each route, accessible via `useMatch`
     * We could test for page access here as well (authenticated but not authorized)
     */
    return { authenticated, user, lastLocationId };
  },
});

/**
 * Fetch user data but ignore failures because no token found. This is called on all routes, even public routes.
 * Public routes have no concept of a logged in user, so this may fail. This makes it fail gracefully.
 */
async function fetchUser(): Promise<PortalUser | undefined> {
  try {
    return await authnClient.fetchUser();
  } catch (error) {
    console.warn('Error fetching user in __root route. Ignore this if public route.', error);
    return undefined;
  }
}
